import PolicyPage from "@/components/shared/PolicyPage";
import { COMPANY, GRIEVANCE, POLICY } from "@/config/company";

const sections = [
  {
    h: "Who we are",
    p: [`${COMPANY.brand} is a brand of ${COMPANY.legalName} (CIN ${COMPANY.cin}, GSTIN ${COMPANY.gstin}), registered at ${COMPANY.addressOneLine}. We are the data fiduciary for the personal data described in this policy.`],
  },
  {
    h: "What data we collect and why",
    ul: [
      "Account and contact details (name, phone number, email, business name, GSTIN): to create your account, verify you and contact you about your shipments.",
      "Shipment details (sender and receiver names, phones, addresses, pincodes, package contents and value, invoice and e-way bill details): to book, route, deliver and invoice your shipments and to meet legal requirements.",
      "Payment and wallet records (amounts, order and transaction references; card and UPI details are handled by our payment partner and are not stored by us): to process payments, refunds and invoices.",
      "KYC and tax documents you upload (for example GST certificate, PAN): to verify business customers and issue compliant invoices.",
      "Delivery, tracking and proof-of-delivery information: to show you shipment status and resolve delivery issues.",
      "Messages and call records with our support team, and technical data such as device, browser and IP address: to answer your queries, prevent fraud and keep the service secure.",
    ],
  },
  {
    h: "WhatsApp and other messages",
    p: [
      "We use WhatsApp, SMS and email to send you booking confirmations, pickup and delivery updates, payment and invoice notices and, only if you have agreed, updates and offers.",
      "WhatsApp messages are sent through the WhatsApp Business Platform. Meta Platforms, Inc. and its group companies process these messages on our behalf and under their own terms; the content and the metadata of the conversation pass through their systems.",
      "Promotional WhatsApp messages are sent only to people who ticked the WhatsApp consent box on our forms. We record the time and the exact wording you agreed to.",
    ],
  },
  {
    h: "How to opt out",
    p: [
      "Reply STOP to any WhatsApp or SMS message from us and we will stop sending you promotional messages. You can also email " + COMPANY.email + " or call " + COMPANY.phone + ".",
      "Service messages about a shipment you have booked (for example a delivery attempt) may still be sent, because they are needed to carry out the service you asked for.",
    ],
  },
  {
    h: "Who we share data with",
    p: ["We do not sell your personal data. We share it only as needed to provide the service, with:"],
    ul: [
      "Courier and logistics partners (including Delhivery) and local delivery staff, to pick up, move and deliver your shipment.",
      "Payment service providers (including Cashfree), to collect payments and make refunds.",
      "Messaging providers (Meta for WhatsApp, and our SMS provider), to deliver messages.",
      "Cloud hosting and storage providers in India, to run the platform and store documents.",
      "Tax, legal and regulatory authorities, auditors and professional advisers, when the law requires or to protect our rights.",
    ],
  },
  {
    h: "How long we keep data",
    ul: [
      `Account and shipment data: ${POLICY.retentionAccount}.`,
      `Marketing and WhatsApp consent records: ${POLICY.retentionMarketing}; we keep the record of the consent itself and of any opt-out so that we can prove we honoured it.`,
      "When data is no longer needed for these purposes we delete it or make it anonymous.",
    ],
  },
  {
    h: "Your rights under the Digital Personal Data Protection Act, 2023",
    p: ["You have the right to:"],
    ul: [
      "obtain a summary of the personal data we hold about you and the others it is shared with;",
      "ask us to correct inaccurate data or complete incomplete data;",
      "ask us to erase data we no longer need to keep by law;",
      "withdraw your consent at any time (withdrawal does not affect what we did before you withdrew);",
      "have your grievance heard and resolved, and nominate someone to exercise these rights for you if you cannot;",
      "complain to the Data Protection Board of India if you are not satisfied with our response.",
    ],
  },
  {
    h: "Security",
    p: ["We use access controls, encryption in transit, restricted staff access and audit logs to protect your data. No system is completely secure, so please keep your password private and tell us at once if you suspect misuse of your account."],
  },
  {
    h: "Children",
    p: ["Our services are meant for adults and businesses. We do not knowingly collect personal data of children without a parent's or guardian's consent."],
  },
  {
    h: "Grievance Officer",
    p: [
      `${GRIEVANCE.name}, ${GRIEVANCE.designation}`,
      `Email: ${GRIEVANCE.email} · Phone: ${COMPANY.phone}`,
      `Address: ${COMPANY.legalName}, ${COMPANY.addressOneLine}`,
      GRIEVANCE.responseNote,
    ],
  },
  {
    h: "Changes to this policy",
    p: [`We may update this policy. The date at the top shows when it last changed (${POLICY.effectiveDate}); material changes will be shown on our website or sent to you.`],
  },
];

export default function PrivacyPolicy() {
  return (
    <PolicyPage
      testId="privacy-policy-page"
      title="Privacy Policy"
      intro="This policy explains what personal data we collect when you use sendtoo.in, our app or our services, why we collect it, who sees it and what choices you have."
      sections={sections}
    />
  );
}
